import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, ArrowDown, Heart, Flag, Trash2, Zap, MoreHorizontal, CornerUpLeft, Check, CheckCheck } from "lucide-react";
import generateAvatarUrl from "../../utils/generateAvatarUrl";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { formatAppDate } from "../../utils/appTimeFormat";
import ChatPollCard from "./ChatPollCard";
import ChatChallengeCard from "./ChatChallengeCard";
import FixtureTagChips from "./FixtureTagChips";
import ReactionChips from "./ReactionChips";
import ReplyQuote from "./ReplyQuote";
import MessageActionMenu, { QUICK_REACTIONS } from "./MessageActionMenu";
import {
  idEq,
  collectMarkReadIds,
  countReadByOthers,
  isIncomingMessage,
  isMessageReadByUser,
  findFirstUnreadAfterCursor,
  countMessagesAfterCursor,
  pickReadCursorFromVisible,
} from "../../utils/bondchat/chatReadHelpers";

const GROUP_MS = 3 * 60 * 1000;

const dayLabel = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return formatAppDate(d);
};

const authorId = (m) => String(m?.userId?._id ?? m?.userId ?? "");

const hasChallenge = (m) =>
  !!(m?.chatChallenge?.p2pBetId && m?.chatChallenge?.fixtureRef);

const sameGroup = (a, b) => {
  if (!a || !b) return false;
  if (authorId(a) !== authorId(b)) return false;
  if (a.poll?.question || b.poll?.question) return false;
  if (hasChallenge(a) || hasChallenge(b)) return false;
  if (new Date(a.createdAt).toDateString() !== new Date(b.createdAt).toDateString()) return false;
  return Math.abs(new Date(b.createdAt) - new Date(a.createdAt)) <= GROUP_MS;
};

const MessageTimeline = ({
  messages,
  userId,
  loading,
  loadingMore,
  hasMore,
  hasNewer = false,
  onLoadOlder,
  onLoadNewer,
  onLike,
  onReact,
  onReply,
  onCounterChallenge,
  onVotePoll,
  onReport,
  onDelete,
  onMarkRead,
  lastReadMessageId = null,
  readStateLoaded = true,
  entryUnreadCount = 0,
  compact = false,
  emptyText = "Be the first to drop a take — the crowd is waiting.",
  feedKey = "all",
  pinToLatest = false,
}) => {
  const listRef = useRef(null);
  const msgRefs = useRef(new Map());
  const pendingReadIds = useRef(new Set());
  const flushReadTimer = useRef(null);
  const observerRef = useRef(null);
  const [atBottom, setAtBottom] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const [menuState, setMenuState] = useState(null);
  const prevLen = useRef(messages.length);
  const initialPositionDoneRef = useRef(false);
  const suppressMarkReadRef = useRef(true);
  const readCursorTimeRef = useRef(0);
  const lastQueuedCursorRef = useRef(null);
  const onMarkReadRef = useRef(onMarkRead);
  onMarkReadRef.current = onMarkRead;

  useEffect(() => {
    initialPositionDoneRef.current = false;
    suppressMarkReadRef.current = true;
    prevLen.current = 0;
    setAtBottom(true);
    setNewCount(0);
    const el = listRef.current;
    if (el) el.scrollTop = 0;
  }, [feedKey]);

  const firstUnreadIdx = useMemo(
    () => findFirstUnreadAfterCursor(messages, userId, lastReadMessageId),
    [messages, userId, lastReadMessageId]
  );

  useEffect(() => {
    const cursorMessage = messages.find(
      (message) => String(message._id || message.tempId) === String(lastReadMessageId)
    );
    readCursorTimeRef.current = cursorMessage
      ? new Date(cursorMessage.createdAt).getTime()
      : 0;
    lastQueuedCursorRef.current = lastReadMessageId ? String(lastReadMessageId) : null;
  }, [messages, lastReadMessageId]);

  const flushMarkRead = useCallback(() => {
    if (!onMarkRead) return;
    const batch = [...pendingReadIds.current];
    pendingReadIds.current.clear();
    if (batch.length) onMarkRead(batch);
  }, [onMarkRead]);

  const queueMarkRead = useCallback(
    (ids) => {
      if (!ids?.length) return;
      ids.forEach((id) => pendingReadIds.current.add(id));
      if (flushReadTimer.current) clearTimeout(flushReadTimer.current);
      flushReadTimer.current = setTimeout(flushMarkRead, 350);
    },
    [flushMarkRead]
  );

  useEffect(
    () => () => {
      if (flushReadTimer.current) clearTimeout(flushReadTimer.current);
      flushMarkRead();
    },
    [flushMarkRead]
  );

  useEffect(() => {
    const root = listRef.current;
    if (!root || !onMarkRead) return undefined;

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (suppressMarkReadRef.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .map((e) => messages.find((m) => String(m._id || m.tempId) === e.target.dataset.msgId))
          .filter(Boolean);
        const markingIds = collectMarkReadIds(visible, userId);
        const cursor = pickReadCursorFromVisible(visible, userId, markingIds);
        const cursorMessage = messages.find(
          (message) => String(message._id || message.tempId) === String(cursor)
        );
        const cursorTime = cursorMessage
          ? new Date(cursorMessage.createdAt).getTime()
          : 0;
        const shouldAdvanceCursor =
          cursor &&
          cursorTime >= readCursorTimeRef.current &&
          String(cursor) !== lastQueuedCursorRef.current;

        if (shouldAdvanceCursor) {
          readCursorTimeRef.current = cursorTime;
          lastQueuedCursorRef.current = String(cursor);
          const ids = [...new Set([...pendingReadIds.current, ...markingIds])];
          pendingReadIds.current.clear();
          if (flushReadTimer.current) clearTimeout(flushReadTimer.current);
          onMarkReadRef.current?.(ids, cursor);
        } else {
          queueMarkRead(markingIds);
        }
      },
      { root, threshold: 0.6 }
    );

    msgRefs.current.forEach((el) => {
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [messages, userId, onMarkRead, queueMarkRead]);

  const scrollBottom = useCallback((smooth = false) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  useEffect(() => {
    if (messages.length > prevLen.current && !atBottom && !loadingMore) {
      setNewCount((c) => c + (messages.length - prevLen.current));
    } else if (atBottom) {
      scrollBottom();
      if (!hasNewer) setNewCount(0);
    }
    prevLen.current = messages.length;
  }, [messages.length, atBottom, loadingMore, hasNewer, scrollBottom]);

  useEffect(() => {
    if (
      loading ||
      !readStateLoaded ||
      !messages.length ||
      initialPositionDoneRef.current
    ) {
      return;
    }

    const unreadIdx = findFirstUnreadAfterCursor(messages, userId, lastReadMessageId);
    const cursorIdx = messages.findIndex(
      (message) => String(message._id || message.tempId) === String(lastReadMessageId)
    );
    const hasUnread =
      !pinToLatest && (entryUnreadCount > 0 || unreadIdx >= 0);
    const target = cursorIdx >= 0 ? messages[cursorIdx] : messages[unreadIdx];
    const targetEl = target
      ? msgRefs.current.get(String(target._id || target.tempId))
      : null;

    if (hasUnread && targetEl) {
      targetEl.scrollIntoView({
        block: cursorIdx >= 0 ? "end" : "start",
        behavior: "auto",
      });
      setAtBottom(false);
      setNewCount(
        entryUnreadCount ||
          countMessagesAfterCursor(messages, userId, lastReadMessageId)
      );
    } else {
      scrollBottom();
      setAtBottom(true);
      setNewCount(0);
      suppressMarkReadRef.current = false;
    }
    initialPositionDoneRef.current = true;
  }, [
    loading,
    readStateLoaded,
    messages,
    userId,
    lastReadMessageId,
    entryUnreadCount,
    scrollBottom,
    pinToLatest,
    feedKey,
  ]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setAtBottom(nearBottom);
    if (nearBottom && hasNewer && !loadingMore) onLoadNewer?.();
    if (nearBottom && !hasNewer) setNewCount(0);
    if (el.scrollTop < 60 && hasMore && !loadingMore) onLoadOlder?.();
  };

  const openMenu = (e, msg) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuState({
      message: msg,
      position: { x: e.clientX, y: e.clientY },
    });
  };

  const items = useMemo(() => {
    const rows = [];
    let msgIndex = -1;
    messages.forEach((msg, i) => {
      const prev = messages[i - 1];
      if (!prev || new Date(msg.createdAt).toDateString() !== new Date(prev.createdAt).toDateString()) {
        rows.push({ kind: "day", id: `day-${msg.createdAt}`, label: dayLabel(msg.createdAt) });
      }
      msgIndex += 1;
      const groupedWithPrev = sameGroup(prev, msg);
      const groupedWithNext = sameGroup(msg, messages[i + 1]);
      rows.push({
        kind: "msg",
        id: msg._id || msg.tempId || `m-${i}`,
        msg,
        msgIndex,
        groupedWithPrev,
        groupedWithNext,
        isUnreadDivider:
          !pinToLatest && msgIndex === firstUnreadIdx && firstUnreadIdx >= 0,
      });
    });
    return rows;
  }, [messages, firstUnreadIdx, pinToLatest]);

  if (loading && !messages.length) {
    return (
      <div className="bc-timeline-loading">
        <Loader2 size={22} className="animate-spin" />
        <span>{pinToLatest ? "Loading this feed…" : "Loading the feed…"}</span>
      </div>
    );
  }

  if (!loading && !messages.length) {
    return (
      <div className="bc-timeline-empty">
        <Zap size={32} strokeWidth={1.5} />
        <strong>No pulse yet</strong>
        <span>{emptyText}</span>
      </div>
    );
  }

  return (
    <div className="bc-timeline-wrap">
      {loadingMore && (
        <div className="bc-timeline-load-more">
          <Loader2 size={14} className="animate-spin" /> Loading earlier…
        </div>
      )}
      <div
        ref={listRef}
        className={`bc-timeline bc-pulse-feed ${compact ? "bc-pulse-feed-compact" : ""}`}
        onScroll={onScroll}
        onWheel={() => {
          suppressMarkReadRef.current = false;
        }}
        onTouchStart={() => {
          suppressMarkReadRef.current = false;
        }}
      >
        {items.map((row) => {
          if (row.kind === "day") {
            return (
              <div key={row.id} className="bc-day-sep">
                <span>{row.label}</span>
              </div>
            );
          }

          if (row.isUnreadDivider) {
            return (
              <React.Fragment key={`${row.id}-wrap`}>
                <div className="bc-unread-rail">
                  <span>
                    {entryUnreadCount > 0
                      ? `${entryUnreadCount} unread message${entryUnreadCount === 1 ? "" : "s"}`
                      : "New messages"}
                  </span>
                </div>
                {renderMessageRow(row)}
              </React.Fragment>
            );
          }

          return renderMessageRow(row);
        })}
      </div>

      {newCount > 0 && (
        <button
          type="button"
          className="bc-new-pill bc-pulse-new"
          onClick={() => {
            suppressMarkReadRef.current = false;
            scrollBottom(true);
            if (hasNewer) onLoadNewer?.();
            if (!hasNewer) setNewCount(0);
          }}
        >
          <ArrowDown size={13} />
          {newCount} unread below
        </button>
      )}

      <MessageActionMenu
        message={menuState?.message}
        userId={userId}
        position={menuState?.position}
        onClose={() => setMenuState(null)}
        onReact={onReact}
        onReply={onReply}
        onReport={onReport}
        onDelete={onDelete}
      />
    </div>
  );

  function renderMessageRow(row) {
    const { msg, groupedWithPrev, groupedWithNext } = row;
    const rowKey = row.id;
    const msgAuthorId = msg.userId?._id ?? msg.userId;
    const isMine = userId && String(msgAuthorId) === String(userId);
    const authorName = msg.userId?.name || "Bettor";
    const liked = (msg.likes || []).some((l) => idEq(l, userId));
    const likeCount = msg.likes?.length || 0;
    const isHot = likeCount >= 3;
    const msgKey = msg._id || msg.tempId;
    const isHovered = hoveredId === rowKey;
    const readByOthers = countReadByOthers(msg, userId);
    const incomingUnread =
      isIncomingMessage(msg, userId) && !isMessageReadByUser(msg, userId);

    return (
      <article
        key={rowKey}
        ref={(el) => {
          if (el) msgRefs.current.set(rowKey, el);
          else msgRefs.current.delete(rowKey);
        }}
        data-msg-id={msgKey}
        className={[
          "bc-pulse-card",
          isMine ? "bc-pulse-card-mine" : "",
          isHot ? "bc-pulse-card-hot" : "",
          groupedWithPrev ? "bc-pulse-card-grouped" : "",
          incomingUnread ? "bc-pulse-card-unread" : "",
          hasChallenge(msg) ? "bc-pulse-card-challenge" : "",
        ].filter(Boolean).join(" ")}
        onMouseEnter={() => setHoveredId(rowKey)}
        onMouseLeave={() => setHoveredId((cur) => (cur === rowKey ? null : cur))}
        onContextMenu={(e) => openMenu(e, msg)}
      >
        {isHovered && (
          <div className={`bc-hover-react ${isMine ? "bc-hover-react-mine" : ""}`}>
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="bc-hover-react-btn"
                onClick={() => onReact?.(msgKey, emoji)}
                aria-label={`React with ${emoji}`}
              >
                {emoji}
              </button>
            ))}
            {onReply && msg._id && (
              <button
                type="button"
                className="bc-hover-react-btn bc-hover-react-action"
                onClick={() => onReply(msg)}
                aria-label="Reply"
              >
                <CornerUpLeft size={13} />
              </button>
            )}
          </div>
        )}

        {!groupedWithPrev && (
          <header className="bc-pulse-card-head">
            <img src={generateAvatarUrl(authorName)} alt="" className="bc-pulse-avatar" />
            <div className="bc-pulse-meta">
              <span className="bc-pulse-author">{isMine ? "You" : authorName}</span>
              <span className="bc-pulse-dot">·</span>
              <span className="bc-pulse-time">{formatRelativeTime(msg.createdAt)}</span>
            </div>
            {isHot && (
              <span className="bc-pulse-hot-badge">
                <Zap size={10} fill="currentColor" /> Hot
              </span>
            )}
            <button
              type="button"
              className="bc-pulse-kebab"
              onClick={(e) => openMenu(e, msg)}
              aria-label="Message actions"
            >
              <MoreHorizontal size={14} />
            </button>
          </header>
        )}

        <div
          className={`bc-pulse-body${
            hasChallenge(msg) ? " bc-pulse-body-challenge" : ""
          }`}
        >
          {hasChallenge(msg) ? (
            <ChatChallengeCard
              msg={msg}
              userId={userId}
              onCounter={onCounterChallenge}
            />
          ) : msg.poll?.question ? (
            <>
              {!!msg.replyTo && <ReplyQuote replyTo={msg.replyTo} onDark={isMine} />}
              <ChatPollCard msg={msg} userId={userId} onVote={onVotePoll} isMine={isMine} />
              {msg.fixtureRefs?.length > 0 && (
                <FixtureTagChips refs={msg.fixtureRefs} isMine={isMine} variant="timeline" />
              )}
            </>
          ) : (
            <>
              {!!msg.replyTo && <ReplyQuote replyTo={msg.replyTo} onDark={isMine} />}
              {!!msg.message?.trim() && <p className="bc-pulse-text">{msg.message}</p>}
              {msg.fixtureRefs?.length > 0 && (
                <FixtureTagChips refs={msg.fixtureRefs} isMine={isMine} variant="timeline" />
              )}
            </>
          )}
        </div>

        <ReactionChips
          reactions={msg.reactions}
          userId={userId}
          onToggle={(emoji) => onReact?.(msgKey, emoji)}
          alignEnd={isMine}
        />

        {!groupedWithNext && (
          <footer className="bc-pulse-reactions">
            <button
              type="button"
              className={`bc-pulse-react ${liked ? "bc-pulse-react-active" : ""}`}
              onClick={() => onLike?.(msgKey)}
            >
              <Heart size={15} fill={liked ? "currentColor" : "none"} />
              <span>{likeCount > 0 ? likeCount : "Pulse"}</span>
            </button>
            {onReply && msg._id && (
              <button type="button" className="bc-pulse-action" onClick={() => onReply(msg)} aria-label="Reply">
                <CornerUpLeft size={13} />
              </button>
            )}
            {!isMine && (
              <button
                type="button"
                className="bc-pulse-action"
                onClick={() => onReport?.(msgKey)}
                aria-label="Report"
              >
                <Flag size={13} />
              </button>
            )}
            {isMine && msg._id && (
              <>
                <span className="bc-read-receipt" title={readByOthers > 0 ? "Seen" : "Sent"}>
                  {readByOthers > 0 ? <CheckCheck size={12} /> : <Check size={12} />}
                  {readByOthers > 0 && <span>Seen</span>}
                </span>
                <button
                  type="button"
                  className="bc-pulse-action"
                  onClick={() => onDelete?.(msg._id)}
                  aria-label="Delete"
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </footer>
        )}
      </article>
    );
  }
};

export default MessageTimeline;
